/**
 * "Make it make sense" — the campaign's latent truth, on a button.
 *
 * Pressed between sessions. The model reads everything the campaign is —
 * premise, canon, every recap and transcript — and constructs the one
 * underlying state of the world that accounts for the loose ends. The result
 * lands here to be read, and in canon to be played toward.
 *
 * The generation takes about five minutes, so this polls while pending and
 * says so plainly; a button that answers nothing for five minutes is the
 * failure every other button in this app has already had to unlearn.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { CampaignSynthesis } from '@/api/client';
import { getSynthesis, startSynthesis } from '@/api/client';
import { Markdown } from '@/components/Markdown';

const POLL_MS = 10_000;

export function WorldTruth({ campaignId }: { campaignId: string }) {
  const [synthesis, setSynthesis] = useState<CampaignSynthesis | null>(null);
  const [starting, setStarting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      const { synthesis: latest } = await getSynthesis(campaignId);
      setSynthesis(latest);
      window.clearTimeout(timer.current);
      if (latest?.status === 'pending') {
        timer.current = window.setTimeout(() => void refresh(), POLL_MS);
      }
    } catch {
      // A campaign without the feature's history is normal; stay quiet.
    }
  }, [campaignId]);

  useEffect(() => {
    setSynthesis(null);
    setExpanded(false);
    void refresh();
    return () => window.clearTimeout(timer.current);
  }, [refresh]);

  async function handleStart() {
    setStarting(true);
    try {
      await startSynthesis(campaignId);
      await refresh();
    } catch {
      /* the GET on next refresh reports the state either way */
    } finally {
      setStarting(false);
    }
  }

  const pending = synthesis?.status === 'pending';
  const complete = synthesis?.status === 'complete';

  return (
    <section className="card">
      <header className="card__head">
        <h2 className="card__title">Make it make sense</h2>
        {complete && synthesis?.generated_at ? (
          <span className="badge badge--ok">
            {new Date(synthesis.generated_at).toLocaleDateString()}
          </span>
        ) : null}
      </header>

      <p className="card__body card__lede">
        Throughline reads the whole campaign and works out the one hidden truth
        that ties your loose ends together. It becomes canon, and every
        session after it plays toward the reveals.
      </p>

      {synthesis?.status === 'failed' ? (
        <div className="notice notice--error">
          That attempt didn’t finish. Nothing in your campaign changed; try
          again when you’re ready.
        </div>
      ) : null}

      {pending ? (
        <p className="card__body card__waiting">
          <span className="spinner" aria-hidden="true" /> Reading the whole
          campaign and working out what is really going on. It takes about
          five minutes, and this page updates on its own.
        </p>
      ) : (
        <button
          type="button"
          className="button"
          onClick={() => void handleStart()}
          disabled={starting}
        >
          {starting
            ? 'Starting…'
            : complete
              ? 'Run it again with everything new'
              : 'Make it make sense'}
        </button>
      )}

      {complete && synthesis ? (
        <>
          {!expanded ? (
            <button
              type="button"
              className="button"
              onClick={() => setExpanded(true)}
            >
              Read it
            </button>
          ) : (
            <div className="truth">
              {synthesis.images.length ? (
                <div className="truth__images">
                  {synthesis.images.map((img) => (
                    <figure key={img.url} className="truth__figure">
                      <img src={img.url} alt={img.caption} loading="lazy" />
                      <figcaption>{img.caption}</figcaption>
                    </figure>
                  ))}
                </div>
              ) : null}
              <Markdown md={synthesis.summary_md} />
              <p className="truth__note">
                This is canon now. Throughline steers play toward these
                reveals, and your players discover them at the table.
              </p>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
